import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";

import { resolveBrowserLocationLabel } from "utils/geolocation";
import { AUTH_PATHS, type AuthFlowState, type AuthNotice, type LoginChallenge } from "./types";

interface AuthFlowContextValue extends AuthFlowState {
  /** Where to go after signing in: the page that sent the user here, else the dashboard. */
  returnPath: string;
  setUserName: (userName: string) => void;
  /** Password accepted and a code was sent: go to the code step. */
  startChallenge: (userName: string, challenge: LoginChallenge) => void;
  updateChallenge: (patch: Partial<LoginChallenge>) => void;
  /** Leave the code or reset steps and show `notice` on the sign-in step. */
  backToSignIn: (notice?: AuthNotice | null) => void;
  startReset: (userName: string, sentMessage: string | null) => void;
  clearNotice: () => void;
  /** Browser location for the audit log; resolved in the background, never blocks sign-in. */
  beginLocationLookup: () => void;
  readLocation: () => string | null;
}

const AuthFlowContext = React.createContext<AuthFlowContextValue | undefined>(undefined);

const initialNotice = (search: string): AuthNotice | null => {
  const reason = new URLSearchParams(search).get("reason");
  if (reason === "expired") {
    return {
      tone: "info",
      title: "Your session has expired",
      message: "Please sign in again to continue.",
    };
  }
  return null;
};

type FromState = { from?: { pathname: string; search?: string } } | undefined;

const computeReturnPath = (search: string, state: FromState): string => {
  const from = state?.from;
  if (from?.pathname && !from.pathname.startsWith("/credentials")) {
    return `${from.pathname}${from.search || ""}`;
  }
  const next = new URLSearchParams(search).get("next");
  // Only same-site paths: "/x" but not "//evil.example".
  if (next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/credentials")) {
    return next;
  }
  return "/dashboard";
};

export const AuthFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();
  const location = useLocation<FromState>();
  const [returnPath] = React.useState(() => computeReturnPath(location.search, location.state));
  const [state, setState] = React.useState<AuthFlowState>(() => ({
    userName: "",
    challenge: null,
    notice: initialNotice(location.search),
    resetSentMessage: null,
  }));
  const locationLabel = React.useRef<string | null>(null);
  const lookupStarted = React.useRef(false);

  const keepQuery = location.search;

  const value = React.useMemo<AuthFlowContextValue>(
    () => ({
      ...state,
      returnPath,
      setUserName: (userName) => setState((prev) => ({ ...prev, userName })),
      startChallenge: (userName, challenge) => {
        setState((prev) => ({ ...prev, userName, challenge, notice: null }));
        history.push(`${AUTH_PATHS.verify}${keepQuery}`);
      },
      updateChallenge: (patch) =>
        setState((prev) =>
          prev.challenge ? { ...prev, challenge: { ...prev.challenge, ...patch } } : prev
        ),
      backToSignIn: (notice = null) => {
        setState((prev) => ({ ...prev, challenge: null, resetSentMessage: null, notice }));
        history.push(`${AUTH_PATHS.signIn}${keepQuery}`);
      },
      startReset: (userName, resetSentMessage) => {
        setState((prev) => ({ ...prev, userName, resetSentMessage, notice: null }));
        history.push(`${AUTH_PATHS.reset}${keepQuery}`);
      },
      clearNotice: () => setState((prev) => ({ ...prev, notice: null })),
      beginLocationLookup: () => {
        if (lookupStarted.current) {
          return;
        }
        lookupStarted.current = true;
        resolveBrowserLocationLabel()
          .then((label) => {
            locationLabel.current = label;
          })
          .catch(() => undefined);
      },
      readLocation: () => locationLabel.current,
    }),
    [state, returnPath, history, keepQuery]
  );

  return <AuthFlowContext.Provider value={value}>{children}</AuthFlowContext.Provider>;
};

export const useAuthFlow = (): AuthFlowContextValue => {
  const ctx = React.useContext(AuthFlowContext);
  if (!ctx) {
    throw new Error("useAuthFlow must be used within AuthFlowProvider");
  }
  return ctx;
};
